// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
    useSelector
} from 'react-redux';
import i18next from 'i18next';
import tooltipIcon from './../../../assets/tooltip-copy-3@2x.png';
import enableCountriesConfig from './../../../lib/OnDemandEnableConfig';

import {
    ChoiceGroup,
    ComboBox,
    IChoiceGroupOption,
    IChoiceGroupStyles,
    Icon,
    IComboBoxOption,
    IComboBoxStyles,
    ITextFieldStyles,
    Spinner,
    SpinnerSize,
    TextField,
    TooltipHost,
    SelectableOptionMenuItemType,
    Checkbox,
} from 'office-ui-fabric-react';
import { sharedComboBoxOptionStyles } from 'src/helpers/sharedComboBoxOptionStyles';
import { safeTrim } from 'src/lib/stringUtil';
import { IDropdownOption } from 'office-ui-fabric-react/lib/Dropdown';
import { DatePicker, DayOfWeek } from 'office-ui-fabric-react/lib/DatePicker';
import { IState } from './../../../views/warrantyUpdate/WarrantyUpdate';
import { GetDatePickerTranslations, onFormatDate } from './../../../helpers/datePickerTranslations';

import ErrorMessage from './../../../components/ErrorMessage/ErrorMessage';
import RepairItemDetail from './../../../components/RepairItemDetail/RepairItemDetail';

import {
    LoadingState,
    actionStatusSelector,
} from './../../../store/actionStatus';

import {
    devicePartSelector,
    deviceSymptomSelector,
    fullDeviceEligibilitySelector,
    partDeviceEligibilitySelector,
} from './../../../store/deviceInfo/selectors';

import { DESCRIPTION_REQUIRED_PROBLEM_TYPE } from './../../../views/createOrder/SelectRepairItems/SelectRepairItems';

import Styles from './styles.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import AddressFields from '../Address/AddressFields';
import { countryListSelector } from '../Address/selector';
import { provincesSelector } from './../../../store/provinces';
import { userAgentTierSelector, userInfoSelector, selectedOptionSelector } from './../../../store/user';
import {
    GET_DEVICE_ELIGIBILITY_DATA,
} from './../../../store/deviceInfo/eligibility';
import Dropzone from 'react-dropzone';
import { checkIsUserEnterpriseCategoryFromTier, checkIsSoftwareSupportEnabledFromTier } from './../../../lib/userTierConfig';
import SoftwareSupport from '../SoftwareSupport';

export interface ProvinceType {
    ISOCode: string | number;
    ISOName: string;
}

const ChoiceGroupStyle: Partial<IChoiceGroupStyles> = {
    root: {
        marginTop: 20,
    },
};
const ComboBoxStyle: Partial<IComboBoxStyles> = {
    container: {
        marginTop: 20,
    },
    root: {
        width: 244,
    },
    optionsContainer: {
        maxHeight: 300,
        width: 243,
    },
};

const PartComboBoxStyle: Partial<IComboBoxStyles> = {
    container: {
        marginTop: 20,
        float: 'left',
        minWidth: '400px',
    },
    root: {
        width: 244,
    },
    optionsContainer: {
        maxHeight: 300,
        width: 243,
    },
};


const TextFieldStyle: Partial<ITextFieldStyles> = {
    root: {
        width: 520,
        marginTop: 20,
    },
};

const checkboxStyles = () => {
    return {
        root: {
            marginTop: '30px',
        },
    };
};

const onDemandEnableCountries: any = enableCountriesConfig.onDemandEnableCountries;
const onCruEnableCountries: any = enableCountriesConfig.onCruEnableCountries;

interface ProblemSelectionProps {
    userTier: any;
    deviceProblemDescription?: string;
    errorMessage?: string;
    onParentProblemDescriptionChange: (value: string | undefined) => void;
    onParentProblemTypeChange: (value: IComboBoxOption | undefined) => void;
    onPartSelectionChange: (options: IComboBoxOption | undefined) => void;
    onDemandFruPartSelectionChange: (options: IComboBoxOption | undefined) => void;
    isCruPartDeviceEligible?: boolean;
    onCruPartDeviceEligibleChange: (value: boolean | false) => void;
    cruProofOfPurchaseSelected?: boolean;
    onCruProofOfPurchaseCheckboxChange: (value: boolean | false) => void;

    onReplaceComponentChange: (option: IChoiceGroupOption | undefined) => void;
    onSoftwareSupportEnabled: (option: IChoiceGroupOption | undefined) => void;
    isSdtToolRan: (option: IDropdownOption | undefined) => void;
    typeOfResolution: (option: IDropdownOption | undefined) => void;
    parent: {
        name: string,
        sku: string,
        serialNumber: string,
    };
    partOrFull?: IChoiceGroupOption;
    selectedParts: any;
    selectedOnDemandFruParts: any;
    selectedProblemType?: IComboBoxOption;
    onSelectAttachment?: any;
    onSelectPurchaseDate?: any;
    checkSymptomCode?: any;
    setCountryOnChange?: any;
    checkSafetySymptom?: any;
    addressValues?: any;
    noOfRepairAttempts?: IComboBoxOption;
    onNoOfRepairAttemptsChange: (value: IComboBoxOption | undefined) => void;
    repairDetailsDescription?: string;
    onRepairDetailsDescriptionChange: (value: string | undefined) => void;
}

export default function ProblemSelection(props: ProblemSelectionProps) {
    const {
        deviceProblemDescription,
        parent,
        selectedProblemType,
        noOfRepairAttempts,
        repairDetailsDescription,
    } = props;

    if (!parent) {
        return null;
    }

    const maxLengthInComments = 512;
    const { t } = useTranslation();
    const actionStatus = useSelector(state => actionStatusSelector(state)(GET_DEVICE_ELIGIBILITY_DATA));
    const isLoading = actionStatus.status === LoadingState.started;
    const isFailed = actionStatus.status === LoadingState.rejected;
    const problemTypeRef = React.useRef<any>(null);
    const [isRRTOptionSelected, setIsRRTOptionSelected] = React.useState(false);

    const userAgentTier = useSelector(state => userAgentTierSelector(state));
    //to get the current tier information and check whether Enterprise category or not
    const isEnterpriseCustomer = checkIsUserEnterpriseCategoryFromTier(userAgentTier);
    const selectedViewOption = useSelector(state => selectedOptionSelector(state));

    const [values, setValues] = React.useState<IState>({
        extension: '',
        firstDayOfWeek: DayOfWeek.Sunday,
        isWarrantyUpdated: false,
        purchaseDate: null,
        DayPickerStrings: GetDatePickerTranslations(),
    });
    const { firstDayOfWeek, purchaseDate, DayPickerStrings } = values;

    React.useEffect(() => {
        if (!isLoading && problemTypeRef.current) {
            problemTypeRef.current.focus();
        }
    }, [problemTypeRef, isLoading]);

    const problemTypeOptions: IComboBoxOption[] = useSelector(state =>
        deviceSymptomSelector(state)
    ).map((symptom: any) => ({
        key: symptom.offerToken,
        text: symptom.name,
        data: symptom,
    })).sort((part: IComboBoxOption, part2: IComboBoxOption) =>
        part.text.localeCompare(part2.text)
    );
    const onSelectDate = (date: Date | null | undefined): void => {
        setValues({ ...values, purchaseDate: date });
        props.onSelectPurchaseDate(date);
    };

    const onParseDateFromString = (value: string): Date => {
        const date = purchaseDate || new Date();
        const values = (value || '').trim().split('/');
        const day = values.length > 0 ? Math.max(1, Math.min(31, parseInt(values[0], 10))) : date.getDate();
        const month = values.length > 1 ? Math.max(1, Math.min(12, parseInt(values[1], 10))) - 1 : date.getMonth();
        let year = values.length > 2 ? parseInt(values[2], 10) : date.getFullYear();
        if (year < 100) {
            year += date.getFullYear() - (date.getFullYear() % 100);
        }
        return new Date(year, month, day);
    };

    const numberOfRepairOptions: IComboBoxOption[] = [
        {
            key: '0',
            text: '0',
            data: '0',
        },
        {
            key: '1',
            text: '1',
            data: '1',
        },
        {
            key: '2',
            text: '2',
            data: '2',
        },
    ];

    function onProblemDescriptionChange(event: any, value: string | undefined) {
        props.onParentProblemDescriptionChange(value);
    }

    function onNoOfRepairsAttemptChange(event: any, value: IComboBoxOption | undefined) {
        logPageAction('No Of Attempts dropdown item selected', { itemSelected: value && value.text });
        props.onNoOfRepairAttemptsChange(value);
    }

    function onRepairDetailsChange(event: any, value: string | undefined) {
        props.onRepairDetailsDescriptionChange(value);
    }

    function getErrorMessage() {
        if (isFailed) {
            return t('something_happened_try_again_later');
        }

        if (!isLoading && !problemTypeOptions.length) {
            return t('no_symptom_found');
        }

        return undefined;
    }

    const problemDescriptionErrorId = 'problemDescriptionError';
    const problemDescriptionHelpTextId = 'problemDescriptionHelpText';

    const conditionalOtherMessage = () => (
        selectedProblemType && selectedProblemType.text
            && selectedProblemType.text.toLowerCase() === DESCRIPTION_REQUIRED_PROBLEM_TYPE
            && (!deviceProblemDescription || deviceProblemDescription.length < 50)
            ? (
                <span
                    id={problemDescriptionErrorId}
                    className={Styles.inlineError}
                    role="alert"
                    aria-live="assertive"
                >
                    {t('enter_50_characters_for_other')}
                </span>
            )
            : null
    );

    const isProblemDescriptionRequired = selectedProblemType && selectedProblemType.text.toLowerCase() === DESCRIPTION_REQUIRED_PROBLEM_TYPE;
    const problemDescriptionPlaceHolder = isProblemDescriptionRequired
        ? `${t('problem_description_placeholder')} ${t('50_character_required')}`
        : t('problem_description_placeholder');

    const countryOptions = useSelector((state: any) => countryListSelector(state));
    const provinceOptions = useSelector((state: any) => provincesSelector(state)((props.addressValues.country || '').toLocaleLowerCase()));
    const languageOptions = t('lists:languages', { returnObjects: true }) as any[];

    const fullDeviceEligibility = useSelector(state => fullDeviceEligibilitySelector(state));
    const isFullDeviceEligible = fullDeviceEligibility.toLowerCase() === 'eligible';
    const useStateCallbackWrapper = (initilValue: any, callBack: any) => {
        const [state, setState] = React.useState(initilValue);
        React.useEffect(() => callBack(state), [state]);
        return [state, setState];
    };

    const callBack = (acceptedFiles: any) => {
        props.onSelectAttachment(acceptedFiles);
    };

    function updateFields(value: any) {
        props.setCountryOnChange(value);

    }

    const [acceptedFiles, setAcceptedFiles] = useStateCallbackWrapper([], callBack);

    const onDrop = (files: any) => {
        files.splice(4 - acceptedFiles.length);
        files.forEach((element: any) => {
            element.id = `${element.lastModified}${Date.now()}`;
        });
        setAcceptedFiles((acceptedFiles: any) => acceptedFiles.concat(files));
    };

    const removeImage = (id: any) => {
        const removedImages = acceptedFiles.filter((image: any) => image.id !== id);
        setAcceptedFiles(removedImages);
    };

    const maxSize = 4194304;
    const devicePart = useSelector(state => devicePartSelector(state));
    const devicePartsOptions: IComboBoxOption[] = props.selectedProblemType !== undefined && props.selectedProblemType !== null ? generatePartOptions(devicePart, props.selectedProblemType, t) : [];
    const [holdingInventoryOptions, setHoldingInventoryOptions] = React.useState<any>(devicePartsOptions);
    const [onDemandInventoryOptions, setOnDemandInventoryOptions] = React.useState<any>(devicePartsOptions);

    function updateHoldingInventoryOptions(options: any) {
        setHoldingInventoryOptions(options);
    }

    function updateOnDemandInventoryOptions(options: any) {
        setOnDemandInventoryOptions(options);
    }

    function onProblemTypeChange(
        event: any,
        value: IComboBoxOption | any
    ) {
        logPageAction('Problem type dropdown item selected', { itemSelected: value && value.text });
        props.onParentProblemTypeChange(value);
        setIsRRTOptionSelected(value.data?.isSafetySymptom);
        props.checkSafetySymptom(value.data?.isSafetySymptom);
        props.onNoOfRepairAttemptsChange(undefined);
        props.onRepairDetailsDescriptionChange('');
    }

    useEffect(() => {
        updateHoldingInventoryOptions(devicePartsOptions);
        updateOnDemandInventoryOptions(devicePartsOptions);
    }, [selectedProblemType]);

    const isAnyPartAvailable: boolean = devicePartsOptions.length > 0 && !enableCountriesConfig.onCruEnableTiers.includes(userAgentTier);

    function getLangOption() {
        if (languageOptions instanceof Array) {
            return languageOptions
                .map((lan: { code: string, lcid: string, name: string }) => ({ key: lan.code.toLowerCase(), text: lan.name } as IDropdownOption));
        }
        return false;
    }

    const renderCustomLabel = (props: any, iconName: string, iconTitle: string, idOfLabel: string) => {
        if (!iconTitle) {
            iconTitle = props.required && props.label === t('problem_description') ? t('problem_desc_others', { fieldName: props.label }) : t('comments_length', { fieldName: props.label })
        }
        return (
            <span className={Styles.problemDescLabel} >
                <span className={Styles.fieldName} id={idOfLabel}>{props.label}</span>
                {props.required && <span className={Styles.problemDescRequired}> *</span>}
                <span
                    className={Styles.problemDescIconWrapper}
                    tabIndex={0}
                    role="img"
                    aria-label={iconTitle}
                    title={iconTitle}
                >
                    <Icon
                        iconName={iconName}
                        aria-hidden={true}
                        className={Styles.problemDescIcon}
                    />
                </span>
            </span>
        );
    };

    return (
        <section className={Styles.parentSection}>
            <h3 className={Styles.h3Heading}>{t('device')}</h3>
            <RepairItemDetail
                data={[
                    {
                        name: parent.name,
                        sku: parent.sku,
                        serialNumber: parent.serialNumber,
                    },
                ]}
                title={t('device')}
            />
            <div className={Styles.comboBox}>
                <ErrorMessage className={Styles.error} message={getErrorMessage()} traceData={{ actionStatus, problemTypeOptions }} />
                <span>
                    <ComboBox
                        ariaLabel={t('problem_type')}
                        disabled={isLoading || !problemTypeOptions.length}
                        dropdownWidth={244}
                        label={t('problem_type')}
                        onChange={onProblemTypeChange}
                        options={problemTypeOptions}
                        componentRef={problemTypeRef}
                        selectedKey={
                            (selectedProblemType && selectedProblemType.key) ||
                            undefined
                        }
                        required={true}
                        openOnKeyboardFocus={true}
                        styles={ComboBoxStyle}
                        comboBoxOptionStyles={sharedComboBoxOptionStyles}
                    />
                    {selectedViewOption === 'create_a_commercial_exchange_order' ?
                        <TooltipHost
                            calloutProps={{ gapSpace: 0 }}
                            hostClassName={Styles.problemTypeToolTip}
                            content={t('problem_type_cru_info_tooltip')}
                        >
                            <img
                                alt={t('problem_type_cru_info_tooltip')}
                                aria-label={t('problem_type_cru_info_tooltip')}
                                className={Styles.probToolTip}
                                src={tooltipIcon}
                            />
                        </TooltipHost> : ''
                    }
                </span>
            </div>
            <LoadingSpinner loading={isLoading} />
            <div className={Styles.commentsFieldsDiv}>
                <span id={problemDescriptionHelpTextId} style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                    {t('problem_description_placeholder')}
                </span>
                <TextField
                    name="problem_desc"
                    styles={TextFieldStyle}
                    label={isRRTOptionSelected ? t('service_agent_inspection_notes') : t('problem_description')}
                    multiline={true}
                    rows={5}
                    value={deviceProblemDescription}
                    onChange={onProblemDescriptionChange}
                    disabled={isLoading || !problemTypeOptions.length}
                    required={isRRTOptionSelected || isProblemDescriptionRequired}
                    placeholder={problemDescriptionPlaceHolder}
                    maxLength={maxLengthInComments}
                    aria-labelledby='problemDescId'
                    aria-describedby={
                        selectedProblemType && selectedProblemType.text
                            && selectedProblemType.text.toLowerCase() === DESCRIPTION_REQUIRED_PROBLEM_TYPE
                            && (!deviceProblemDescription || deviceProblemDescription.length < 50)
                            ? `${problemDescriptionHelpTextId} ${problemDescriptionErrorId}`
                            : problemDescriptionHelpTextId
                    }
                    onRenderLabel={(props) => renderCustomLabel(props, "info", "", 'problemDescId')}
                />
                {conditionalOtherMessage()}
            </div>
            <SelectablePartAndDevice
                selectedProblemType={selectedProblemType}
                devicePartsOptions={devicePartsOptions}
                holdingInventoryOptions={holdingInventoryOptions}
                onDemandInventoryOptions={onDemandInventoryOptions}
                updateHoldingInventoryOptions={updateHoldingInventoryOptions}
                updateOnDemandInventoryOptions={updateOnDemandInventoryOptions}
                {...props}
            />
            <div className={Styles.comboBoxBlock} >
                <ErrorMessage className={Styles.error} />
                <ComboBox role="combobox" hidden={isAnyPartAvailable === false || props.partOrFull === undefined || props.partOrFull.key === 'part'}
                    ariaLabel={t('number_of_repair_attempts')}
                    aria-controls="comboBox-repair-options"
                    aria-expanded="true"
                    dropdownWidth={244}
                    label={t('number_of_repair_attempts')}
                    options={numberOfRepairOptions}
                    styles={ComboBoxStyle}
                    comboBoxOptionStyles={sharedComboBoxOptionStyles}
                    onChange={onNoOfRepairsAttemptChange}
                    required={!(isAnyPartAvailable === false || props.partOrFull === undefined || props.partOrFull.key === 'part')}
                    selectedKey={noOfRepairAttempts?.key}
                    id="comboBox-repair-options"
                />
            </div>
            <div className={isAnyPartAvailable === false || props.partOrFull === undefined || props.partOrFull.key === 'part' ? Styles.hidden : Styles.commentsFieldsDiv}>
                <TextField
                    ariaLabel={t('repair_details')}
                    role="textbox"
                    styles={TextFieldStyle}
                    label={t('repair_details')}
                    multiline={true}
                    rows={5}
                    value={repairDetailsDescription}
                    onChange={onRepairDetailsChange}
                    required={!(isAnyPartAvailable === false || props.partOrFull === undefined || props.partOrFull.key === 'part')}
                    placeholder={t('repair_details_placeholder')}
                />
            </div>
            {props.userTier.showSafetySection && isRRTOptionSelected && (
                <>
                    <div className={clsx(Styles.pstHeaderContainer, 'ms-Grid-row')}>
                        <div className={clsx('ms-Grid-col', 'ms-sm12')}>
                            <div className={Styles.rrtContent}>{t('device_date_of_purchase')}</div>
                            <DatePicker
                                id="enter_purchase_date"
                                ariaLabel={t('enter_purchase_date')}
                                isRequired={false}
                                allowTextInput={true}
                                firstDayOfWeek={firstDayOfWeek}
                                strings={DayPickerStrings}
                                value={purchaseDate!}
                                onSelectDate={onSelectDate}
                                formatDate={onFormatDate}
                                parseDateFromString={onParseDateFromString}
                                placeholder="mm/dd/yyyy"
                                className={Styles.datePickerWidth}
                                disabled={!isFullDeviceEligible}
                            />
                        </div>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm12', Styles.pstHeaderContainer)}>
                        <div className={Styles.rrtContent}>{t('partner_location_address')}</div>
                    </div>
                    <AddressFields
                        isFullDeviceEligible={!isFullDeviceEligible}
                        updateFields={updateFields}
                        {...props.addressValues}

                        disabled={false}
                        isCountryEnable={false}
                        isEnterpriseCustomer={isEnterpriseCustomer}
                        languageOptions={getLangOption()}
                        countryOptions={
                            countryOptions
                                .sort()
                                .map((option: { Id: string | number; ISOName: string; }) => ({ key: option.Id, text: option.ISOName } as IDropdownOption))}
                        provincesOptions={
                            (provinceOptions || [] as ProvinceType[])
                                .sort()
                                .map((option: { ISOCode: string | number; ISOName: string; }) => ({ key: option.ISOCode, text: option.ISOName } as IDropdownOption))}
                    />
                    <div>
                        <div className={Styles.rrtContent}>{t('attachments')}:({t('max_upload_size_and_no_of_files_limit')})</div>

                        <Dropzone
                            aria-label={t('attachments')}
                            onDrop={onDrop}
                            accept="image/png, image/jpeg, image/gif, image/bmp"
                            minSize={0}
                            maxSize={maxSize}
                            disabled={(acceptedFiles && acceptedFiles.length >= 4) || !isFullDeviceEligible}
                            multiple={true}>
                            {(varProps: any) => {
                                const { getRootProps, getInputProps, isDragActive, isDragReject, rejectedFiles } = varProps;
                                const isFileTooLarge = rejectedFiles.length > 0 && rejectedFiles[0].size > maxSize;
                                return (
                                    <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        {acceptedFiles && acceptedFiles.length >= 4 ? t('max_files_limit') :
                                            !isDragActive && <p className={Styles.dropZone}>
                                                {t('click_here_or_drop_a_file_to_upload')} <br />
                                                <div className={Styles.fileFormat}>({t('type_of_files')})</div>
                                            </p>}
                                        {isDragActive &&
                                            !isDragReject &&
                                            <p className={Styles.dropZone}>
                                                {t('drop_it_here')}</p>}
                                        {isDragReject &&
                                            <p className={`${Styles.dropZone} 
                                        ${Styles.notSupportedText}`}>
                                                {t('file_type_not_accepted_sorry')}</p>}
                                        {isFileTooLarge && (
                                            <p className={Styles.inlineError}>
                                                {t('file_is_too_large')}
                                            </p>
                                        )}
                                    </div>
                                );
                            }
                            }
                        </Dropzone>
                        <div>
                            <div className={Styles.imageContainer}>
                                {acceptedFiles.length > 0 && acceptedFiles.map((acceptedFile: any) => (
                                    <div className={Styles.image} key={acceptedFile.id}>
                                        {acceptedFile.name}
                                        {/* tslint:disable-next-line: jsx-no-lambda*/}
                                        <button id="close_btn" onClick={() => removeImage(acceptedFile.id)}>X</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}

export function SelectablePartAndDevice(props: any) {
    if (!props.selectedProblemType) {
        return null;
    }

    const { t } = useTranslation();
    const {
        partOrFull, selectedParts, selectedOnDemandFruParts,
        devicePartsOptions, holdingInventoryOptions,
        onDemandInventoryOptions, updateHoldingInventoryOptions,
        updateOnDemandInventoryOptions, selectedProblemType,
    } = props;
    const isOnDemandPartDeviceEligible = props.selectedProblemType.data.onDemandEligibilityCode;

    const [selectedKey, setSelectedKey] = React.useState<any>(undefined);
    const [selectedSoftwareSupportKey, setSoftwareSupportSelectedKey] = React.useState<any>(undefined);

    const partDeviceEligibility = useSelector(state => partDeviceEligibilitySelector(state));
    const fullDeviceEligibility = useSelector(state => fullDeviceEligibilitySelector(state));

    const isFullDeviceEligible = fullDeviceEligibility.toLowerCase() === 'eligible';
    const isPartDeviceEligible = partDeviceEligibility.toLowerCase() === 'eligible';
    const isCruPartDeviceEligible = props.selectedProblemType.data.cruEligibilityCode;

    const isPartSelected = partOrFull && partOrFull.key === 'part';

    const selectedTypeOption: string[] = selectedParts.map((part: IComboBoxOption) => part.key);
    const selectedOnDemandTypeOption: string[] = selectedOnDemandFruParts.map((part: IComboBoxOption) => part.key);
    const userAgentTier = useSelector(state => userAgentTierSelector(state));
    const agentInfo = useSelector(state => userInfoSelector(state));
    const selectedViewOption = useSelector(state => selectedOptionSelector(state));
    const [sdtToolRan, setSdtToolRan] = React.useState<IDropdownOption | undefined>(undefined);
    const [typeOfResolution, setTypeOfResolution] = React.useState<IDropdownOption | undefined>(undefined);
    const isCruPartSelected = showCruSelection(isCruPartDeviceEligible, userAgentTier, agentInfo);
    const isAnyPartAvailable: boolean = (devicePartsOptions.length > 0 && !enableCountriesConfig.onCruEnableTiers.includes(userAgentTier)) || isCruPartSelected;

    const partOrFullRadioOptions = [
        {
            key: 'full',
            text: t('full_device_replacement'),
            disabled: !isFullDeviceEligible,
            onRenderLabel: !isFullDeviceEligible ? onRenderFullDeviceLabel : undefined,
        },
    ];

    if (isAnyPartAvailable) {
        partOrFullRadioOptions.push({
            key: 'part',
            text: t('part_replacement'),
            disabled: !isPartDeviceEligible,
            onRenderLabel: !isPartDeviceEligible ? onRenderPartDeviceLabel : undefined,
        });
    }
    const softwareSupportRadioOptions = [];
    const isSoftwareSupportNotEligible = !(props.selectedProblemType.data.isSoftwareSupportEnabled);



    if (checkIsSoftwareSupportEnabledFromTier(userAgentTier) && !isSoftwareSupportNotEligible) {
        softwareSupportRadioOptions.push({
            key: 'software_support',
            text: t('software_support'),
            // disabled: isSoftwareSupportNotEligible,
        });
    }

    useEffect(() => {
        setSoftwareSupportSelectedKey(undefined);
    }, [selectedProblemType]);

    useEffect(() => {
        updateHoldingInventoryOptions(devicePartsOptions);
        updateOnDemandInventoryOptions(devicePartsOptions);
    }, [isAnyPartAvailable]);

    function onPartOrFullReplacementChange(event: any, option: IChoiceGroupOption | undefined) {
        if (option) {
            logPageAction('Replacement type radio button selected', { itemSelected: option.text });
        }
        props.onReplaceComponentChange(option);
        if (option) {
            setSelectedKey(option.key);
            setSoftwareSupportSelectedKey(undefined);
            props.isSdtToolRan(undefined);
            props.typeOfResolution(undefined);
            props.onSoftwareSupportEnabled(undefined);
        }
        props.onNoOfRepairAttemptsChange(undefined);
        props.onRepairDetailsDescriptionChange('');
    }

    function onSoftwareSupportEnabled(event: any, option: IChoiceGroupOption | undefined) {
        if (option) {
            logPageAction('Software Support type radio button selected', { itemSelected: option.text });
        }
        props.onSoftwareSupportEnabled(option);
        if (option) {
            setSoftwareSupportSelectedKey(option.key);
            setSelectedKey(undefined);
            props.onReplaceComponentChange(undefined);
        }

    }

    function onSdtToolRanChange(option: IDropdownOption | undefined) {
        setSdtToolRan(option);
        props.isSdtToolRan(option);
    }

    function onTypeOfResolutionChange(option: IDropdownOption | undefined) {
        setTypeOfResolution(option);
        props.typeOfResolution(option);
    }

    function onPartSelection(event: any, options: IComboBoxOption | undefined) {
        logPageAction('Part selected from combo box');
        let filteredholdingInventoryOptions: any;
        if (options?.selected) {
            filteredholdingInventoryOptions = onDemandInventoryOptions.filter(
                (part: IComboBoxOption) => part.key !== options?.key
            );
        } else {
            const optionsVal: any = options;
            const { selected, ...removeSelectedFromOption } = optionsVal;
            filteredholdingInventoryOptions = [...onDemandInventoryOptions, removeSelectedFromOption];
            filteredholdingInventoryOptions = addOptionAfterCheckingAvailableOptions(filteredholdingInventoryOptions);
        }
        updateOnDemandInventoryOptions(filteredholdingInventoryOptions);
        props.onPartSelectionChange(options);
    }

    function onDemandFruPartSelection(event: any, options: IComboBoxOption | undefined) {
        logPageAction('On-Demand Part selected from combo box');
        updateHoldingInventoryOptions(holdingInventoryOptions);
        props.onDemandFruPartSelectionChange(options);
    }

    function onCruPartSelection(event: any) {
        logPageAction('Proof Of Purchase for Commercial Spare is checked');
        updateHoldingInventoryOptions(holdingInventoryOptions);
        props.onCruProofOfPurchaseCheckboxChange(event.target.checked);
    }

    function addOptionAfterCheckingAvailableOptions(devicePartsOptions: any) {
        if (!devicePartsOptions.find((option: any) => option.disabled)) {
            return devicePartsOptions
                .sort((part: any, part2: any) =>
                    part.data.name.localeCompare(part2.data.name)
                );
        }

        const availableOptions = devicePartsOptions
            .filter((option: any) =>
                option.key !== 'Header1' && option.key !== 'divider' && option.key !== 'Header2' && !option.disabled)
            .sort((part: any, part2: any) =>
                part.data.name.localeCompare(part2.data.name)
            );
        const unAvailableOptions = devicePartsOptions
            .filter((option: any) => option.disabled)
            .sort((part: any, part2: any) =>
                part.data.name.localeCompare(part2.data.name)
            );
        return [
            { key: 'Header1', text: t('available_components'), itemType: SelectableOptionMenuItemType.Header },
            ...availableOptions,
            { key: 'divider', text: '-', itemType: SelectableOptionMenuItemType.Divider },
            { key: 'Header2', text: t('unavailable_components'), itemType: SelectableOptionMenuItemType.Header, data: { requiredWarning: true } },
            ...unAvailableOptions,
        ];
    }
    function onRenderPartDeviceLabel(option: IChoiceGroupOption): JSX.Element {
        return (
            <span id="part_replacement_label_parent"
                aria-label={option.text}
                className={Styles.label}>
                {option.text}
                <TooltipHost
                    aria-label={t('order_cannot_be_created', { reason: t(partDeviceEligibility) })}
                    calloutProps={{ gapSpace: 0 }}
                    content={t('order_cannot_be_created', { reason: t(partDeviceEligibility) })}
                    id="part_replacement_label_child"
                >
                    <Icon
                        className={Styles.warningIcon}
                        iconName="warning"
                        aria-label={t('order_cannot_be_created', { reason: t(partDeviceEligibility) })}
                    />
                </TooltipHost>
            </span>
        );
    }

    function onRenderFullDeviceLabel(option: IChoiceGroupOption): JSX.Element {
        return (
            <span id="full_device_replacement_label_parent"
                aria-label={option.text}
                className={Styles.label}>
                {option.text}
                <TooltipHost
                    aria-label={t('order_cannot_be_created', { reason: t(fullDeviceEligibility) })}
                    calloutProps={{ gapSpace: 0 }}
                    content={t('order_cannot_be_created', { reason: t(fullDeviceEligibility) })}
                    id="full_device_replacement_label_child"
                >
                    <Icon
                        className={Styles.warningIcon}
                        iconName="warning"
                        aria-label={t('order_cannot_be_created', { reason: t(fullDeviceEligibility) })}
                    />
                </TooltipHost>
            </span>
        );
    }

    React.useEffect(() => {  
        setSelectedKey(undefined);
        props.onReplaceComponentChange(undefined);
        setSoftwareSupportSelectedKey(undefined);
        props.isSdtToolRan(undefined);
        props.typeOfResolution(undefined);
        props.onSoftwareSupportEnabled(undefined);
        props.onNoOfRepairAttemptsChange(undefined);
        props.onRepairDetailsDescriptionChange('');
    }, [isAnyPartAvailable]);

    function customLabelRenderOption(item: any) {
        return (
            <div className={Styles.menuItemHeader}>
                {item.text}
                {item.data && item.data.requiredWarning &&
                    <TooltipHost
                        className={Styles.toolTip}
                        content={t('contact_crew_support')}
                    >
                        <Icon
                            className={Styles.warningIcon}
                            iconName="warning"
                        />
                    </TooltipHost>
                }
            </div>
        );
    }

    function showOnDemandSelection(isOnDemandPartDeviceEligible: any, userAgentTier: any, agentInfo: any) {
        if (isOnDemandPartDeviceEligible && enableCountriesConfig.onDemandEnableTiers.includes(userAgentTier) && onDemandEnableCountries.includes(safeTrim(agentInfo.countryCode).toUpperCase())) {
            return true;
        }
        return false;
    }

    function showCruSelection(isCruPartDeviceEligible: any, userAgentTier: any, agentInfo: any) {
        if (selectedViewOption === 'create_a_commercial_exchange_order'
            && isCruPartDeviceEligible
            && enableCountriesConfig.onCruEnableTiers.includes(userAgentTier)
            && onCruEnableCountries.includes(safeTrim(agentInfo.countryCode).toUpperCase())
        ) {
            props.onCruPartDeviceEligibleChange(true);
            return true;
        }
        return false;
    }


    return (
        <div className={Styles.parentOrPartSection}>
            <ChoiceGroup
                selectedKey={selectedKey}
                styles={ChoiceGroupStyle}
                name="choiceGroup"
                options={partOrFullRadioOptions}
                onChange={onPartOrFullReplacementChange}
            />
            <ChoiceGroup
                selectedKey={selectedSoftwareSupportKey}
                name="choiceGroup"
                options={softwareSupportRadioOptions}
                onChange={onSoftwareSupportEnabled}
            />
            {isPartSelected && isAnyPartAvailable ? (
                <div>
                    <div>
                        <span className={Styles.partSelectionDiv}>
                            <ComboBox
                                styles={PartComboBoxStyle}
                                selectedKey={selectedTypeOption}
                                options={holdingInventoryOptions}
                                label={isCruPartSelected ? t('replace_component_cru_inventory', {
                                    count: holdingInventoryOptions.length,
                                }) : t('replace_component_inventory', {
                                    count: holdingInventoryOptions.length,
                                })}
                                onRenderOption={customLabelRenderOption}
                                multiSelect={true}
                                comboBoxOptionStyles={sharedComboBoxOptionStyles}
                                onChange={onPartSelection}
                                className={Styles.partSelectionCombo}
                                id="component_inventry_cru"
                            />
                            <TooltipHost
                                hostClassName={isCruPartSelected ? Styles.cruElementToolTip : Styles.elementToolTip}
                                aria-label={isCruPartSelected ? t('replace_component_cru_inventory_toolip_text') : t('replace_component_inventory_toolip_text')}
                                content={isCruPartSelected ? t('replace_component_cru_inventory_toolip_text') : t('replace_component_inventory_toolip_text')}>
                                <img
                                    alt={isCruPartSelected ? t('replace_component_cru_inventory_toolip_text') : t('replace_component_inventory_toolip_text')}
                                    aria-label={isCruPartSelected ? t('replace_component_cru_inventory_toolip_text') : t('replace_component_inventory_toolip_text')}
                                    className={Styles.toolTipIcon}
                                    src={tooltipIcon}
                                    tabIndex={0}
                                    aria-describedby={isCruPartSelected ? t('replace_component_cru_inventory_toolip_text') : t('replace_component_inventory_toolip_text')}
                                />
                            </TooltipHost>
                        </span>
                        {showOnDemandSelection(isOnDemandPartDeviceEligible, userAgentTier, agentInfo) ? (
                            <span className={Styles.partSelectionDiv}>
                                <ComboBox
                                    styles={showOnDemandSelection(isOnDemandPartDeviceEligible, userAgentTier, agentInfo) ? PartComboBoxStyle : ComboBoxStyle}
                                    selectedKey={selectedOnDemandTypeOption}
                                    options={onDemandInventoryOptions}
                                    label={t('replace_component_on_demand_inventory', {
                                        count: onDemandInventoryOptions.length,
                                    })}
                                    onRenderOption={customLabelRenderOption}
                                    multiSelect={true}
                                    comboBoxOptionStyles={sharedComboBoxOptionStyles}
                                    onChange={onDemandFruPartSelection}
                                    className={Styles.onDemandSelectionCombo}
                                />
                                <TooltipHost
                                    hostClassName={Styles.elementToolTip}
                                    aria-label={t('replace_component_on_demand_inventory_toolip_text')}
                                    content={t('replace_component_on_demand_inventory_toolip_text')}>
                                    <img
                                        alt={t('replace_component_on_demand_inventory_toolip_text')}
                                        aria-label={t('replace_component_on_demand_inventory_toolip_text')}
                                        className={Styles.toolTipIcon}
                                        src={tooltipIcon}
                                        tabIndex={0}
                                        aria-describedby={t('replace_component_on_demand_inventory_toolip_text')}
                                    />
                                </TooltipHost>
                            </span>
                        ) : null
                        }
                    </div>
                    {
                        isCruPartSelected ? (
                            <div>
                                <div className={Styles.popCheckboxDiv}>
                                    <Checkbox
                                        label={t('replace_component_cru_inventory_POP')}
                                        styles={checkboxStyles}
                                        checked={props.cruProofOfPurchaseSelected}
                                        onChange={onCruPartSelection}
                                        className={Styles.cruInventoryPopCheckedRequired}
                                    />
                                </div>
                                <TooltipHost
                                    hostClassName={Styles.cruCheckboxElementToolTip}
                                    aria-label={t('replace_component_cru_inventory_POP_toolip_text')}
                                    content={t('replace_component_cru_inventory_POP_toolip_text')}>
                                    <img
                                        alt={t('replace_component_cru_inventory_POP_toolip_text')}
                                        aria-label={t('replace_component_cru_inventory_POP_toolip_text')}
                                        className={Styles.toolTipIcon}
                                        src={tooltipIcon}
                                        tabIndex={0}
                                        aria-describedby={t('replace_component_cru_inventory_POP_toolip_text')}
                                    />
                                </TooltipHost>

                            </div>
                        ) : null
                    }
                </div>
            ) : <div>
                {selectedSoftwareSupportKey !== undefined ? (
                    <div>
                        <SoftwareSupport
                            sdtToolRan={sdtToolRan}
                            typeOfResolution={typeOfResolution}
                            onSdtToolRanChange={onSdtToolRanChange}
                            onTypeOfResolutionChange={onTypeOfResolutionChange}
                        />
                    </div>

                ) : null} </div>}
        </div>
    );
}

export function generatePartOptions(devicePart: any[], selectedProblemType: any, t: i18next.TFunction): IComboBoxOption[] {
    const devicePartsOptions: IComboBoxOption[] = devicePart
        .filter((part: any) =>
            selectedProblemType?.data?.components
                .toLocaleString()
                .toLowerCase()
                .includes(part.symptomCode.toLowerCase())
        )
        .map((part: any) => ({
            key: part.offerToken,
            text: part.name,
            disabled: part.regex && !part.serialNumber,
            data: part,
        }))
        .sort((part: any, part2: any) =>
            part.text.localeCompare(part2.text)
        );

    if (!devicePartsOptions.find((option: any) => option.disabled)) {
        return devicePartsOptions;
    }

    const availableOptions = devicePartsOptions.filter((option: any) => !option.disabled);
    const unAvailableOptions = devicePartsOptions.filter((option: any) => option.disabled);

    return [
        { key: 'Header1', text: t('available_components'), itemType: SelectableOptionMenuItemType.Header },
        ...availableOptions,
        { key: 'divider', text: '-', itemType: SelectableOptionMenuItemType.Divider },
        { key: 'Header2', text: t('unavailable_components'), itemType: SelectableOptionMenuItemType.Header, data: { requiredWarning: true } },
        ...unAvailableOptions,
    ];
}

export function LoadingSpinner({ loading }: { loading: boolean }) {
    return loading ?
        <Spinner
            className={Styles.spinner}
            size={SpinnerSize.medium}
        /> : null;
}
